import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getProductById } from '../utils/api';
import { toast } from 'react-toastify';
import { FiMinus, FiPlus, FiShoppingBag, FiArrowLeft, FiStar, FiTruck, FiShield, FiRefreshCw, FiCheck, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './ProductDetail.css';

// Base URL for serving static files (images)
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') return '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

// Generate consistent pseudo-random rating based on product ID
const generateRating = (productId) => {
  const hash = productId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const rating = 3.9 + (hash % 10) * 0.1;
  const reviews = 30 + (hash % 71);
  return { rating: Math.min(parseFloat(rating.toFixed(1)), 4.8), reviews };
};

// Check for special badges based on product name
const getSpecialBadge = (productName) => {
  const name = productName?.toLowerCase() || '';
  if (name.includes('drools') && name.includes('10kg') && name.includes('chicken & egg')) {
    return { type: 'most-ordered', label: 'Most Ordered' };
  }
  if (name.includes('purepet') && name.includes('10kg') && name.includes('meat & rice')) {
    return { type: 'bestseller', label: 'Bestseller' };
  }
  if (name.includes('chappi') && name.includes('chicken & rice')) {
    return { type: 'bestseller', label: 'Bestseller' };
  }
  return null;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const loadProduct = useCallback(async () => {
    try {
      const data = await getProductById(id);
      setProduct(data.product);
      
      // Auto-select first variant if product has variants
      if (data.product.hasVariants && data.product.variants?.length > 0) {
        setSelectedVariant(data.product.variants[0]);
      }
      
      // Reset selected image when product changes
      setSelectedImageIndex(0);
    } catch (error) {
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleQuantityChange = (delta) => {
    const maxStock = selectedVariant ? selectedVariant.stock : (product?.stock || 10);
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= maxStock) {
      setQuantity(newQuantity);
    }
  };

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setQuantity(1); // Reset quantity when variant changes
  };

  const getCurrentPrice = () => {
    if (selectedVariant) {
      return selectedVariant.price;
    }
    return product?.price || 0;
  };

  const getCurrentStock = () => {
    if (selectedVariant) {
      return selectedVariant.stock;
    }
    return product?.stock || 0;
  };

  const getCurrentWeight = () => {
    if (selectedVariant) {
      return selectedVariant.weight;
    }
    return product?.weight || '';
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    const currentStock = getCurrentStock();
    if (currentStock === 0) {
      toast.error('Product is out of stock');
      return;
    }

    // Create cart item with variant info if applicable
    const cartItem = {
      ...product,
      price: getCurrentPrice(),
      weight: getCurrentWeight(),
      stock: currentStock,
      variantId: selectedVariant?._id || null,
      // Create a unique key for cart item (product + variant combination)
      cartItemId: selectedVariant ? `${product._id}-${selectedVariant._id}` : product._id
    };

    for (let i = 0; i < quantity; i++) {
      addToCart(cartItem);
    }
    // item added
  };

  const handleBuyNow = () => {
    if (!product) return;
    handleAddToCart();
    navigate('/cart');
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/600x600?text=No+Image';
    if (imageUrl.startsWith('/uploads/')) {
      return `${getBaseUrl()}${imageUrl}`;
    }
    return imageUrl;
  };

  // Get all product images
  const getAllImages = () => {
    if (!product) return [];
    
    // If product has images array, use it
    if (product.images && product.images.length > 0) {
      return product.images.map(img => img.url);
    }
    
    // Fallback to single imageUrl
    if (product.imageUrl) {
      return [product.imageUrl];
    }
    
    return [];
  };

  const handleImageSelect = (index) => {
    setSelectedImageIndex(index);
  };

  const handleNextImage = () => {
    const images = getAllImages();
    if (images.length > 0) {
      setSelectedImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const handlePrevImage = () => {
    const images = getAllImages();
    if (images.length > 0) {
      setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const formatCategory = (category) => {
    return category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="not-found">
            <h2>Product Not Found</h2>
            <p>The product you're looking for doesn't exist.</p>
            <Link to="/shop" className="btn btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentStock = getCurrentStock();

  return (
    <div className="product-detail-page">
      <div className="container">
        <button onClick={() => navigate(-1)} className="back-link">
          <FiArrowLeft />
          Back
        </button>

        <div className="product-detail-content">
          {/* Product Image Gallery */}
          <div className="product-image-section">
            <div className="main-image-wrapper">
              <img 
                src={getImageUrl(getAllImages()[selectedImageIndex] || product.imageUrl)} 
                alt={product.name}
                className="main-image"
              />
              {product.featured && (
                <span className="featured-badge">Featured</span>
              )}
              
              {/* Navigation arrows for multiple images */}
              {getAllImages().length > 1 && (
                <>
                  <button 
                    className="image-nav-btn prev" 
                    onClick={handlePrevImage}
                    aria-label="Previous image"
                  >
                    <FiChevronLeft />
                  </button>
                  <button 
                    className="image-nav-btn next" 
                    onClick={handleNextImage}
                    aria-label="Next image"
                  >
                    <FiChevronRight />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {getAllImages().length > 1 && (
              <div className="image-thumbnails">
                {getAllImages().map((imgUrl, index) => (
                  <button
                    key={index}
                    className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                    onClick={() => handleImageSelect(index)}
                  >
                    <img src={getImageUrl(imgUrl)} alt={`${product.name} - ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
            
            {/* Image counter */}
            {getAllImages().length > 1 && (
              <div className="image-counter">
                {selectedImageIndex + 1} / {getAllImages().length}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-info-section">
            {(() => {
              const ratingData = generateRating(product._id);
              const specialBadge = getSpecialBadge(product.name);
              return (
                <>
                  <div className="product-header">
                    <span className="product-category">{formatCategory(product.category)}</span>
                    <div className="product-rating">
                      <div className="stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FiStar 
                            key={star} 
                            className={star <= Math.round(ratingData.rating) ? 'star-filled' : 'star-empty'} 
                          />
                        ))}
                      </div>
                      <span>{ratingData.rating.toFixed(1)}</span>
                      <span className="reviews">({ratingData.reviews} reviews)</span>
                    </div>
                  </div>

                  <h1 className="product-title">{product.name}</h1>
                  
                  {specialBadge && (
                    <span className={`detail-badge badge-${specialBadge.type}`}>
                      {specialBadge.label}
                    </span>
                  )}
                </>
              );
            })()}

            <div className="product-price-section">
              <span className="price">₹{getCurrentPrice().toLocaleString()}</span>
              <span className="weight-badge">{getCurrentWeight()}</span>
            </div>

            {/* Variant Selector */}
            {product.hasVariants && product.variants?.length > 0 && (
              <div className="variant-selector">
                <label className="variant-label">Select Size</label>
                <div className="variant-options">
                  {product.variants.map((variant) => (
                    <button
                      key={variant._id}
                      className={`variant-option ${selectedVariant?._id === variant._id ? 'selected' : ''} ${variant.stock === 0 ? 'out-of-stock' : ''}`}
                      onClick={() => variant.stock > 0 && handleVariantSelect(variant)}
                      disabled={variant.stock === 0}
                    >
                      <span className="variant-weight">{variant.weight}</span>
                      <span className="variant-price">₹{variant.price.toLocaleString()}</span>
                      {selectedVariant?._id === variant._id && (
                        <FiCheck className="variant-check" />
                      )}
                      {variant.stock === 0 && (
                        <span className="variant-oos">Out of Stock</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="stock-status">
              {currentStock === 0 ? (
                <span className="out-of-stock">Out of Stock</span>
              ) : currentStock <= 5 ? (
                <span className="low-stock">Only {currentStock} left in stock</span>
              ) : (
                <span className="in-stock">In Stock</span>
              )}
            </div>

            <div className="product-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>

            {currentStock > 0 && (
              <div className="purchase-section">
                <div className="quantity-selector">
                  <span className="quantity-label">Quantity</span>
                  <div className="quantity-controls">
                    <button 
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      className="quantity-btn"
                    >
                      <FiMinus />
                    </button>
                    <span className="quantity-value">{quantity}</span>
                    <button 
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= currentStock}
                      className="quantity-btn"
                    >
                      <FiPlus />
                    </button>
                  </div>
                </div>

                <div className="action-buttons">
                  <button onClick={handleAddToCart} className="btn btn-outline add-to-cart-btn">
                    <FiShoppingBag />
                    Add to Cart
                  </button>
                  <button onClick={handleBuyNow} className="btn btn-primary buy-now-btn">
                    Buy Now
                  </button>
                </div>
              </div>
            )}

            <div className="product-features">
              <div className="feature">
                <FiTruck />
                <div>
                  <strong>Fast Delivery</strong>
                  <span>Fast delivery to your doorstep</span>
                </div>
              </div>
              <div className="feature">
                <FiShield />
                <div>
                  <strong>Quality Assured</strong>
                  <span>100% authentic products</span>
                </div>
              </div>
              <div className="feature">
                <FiRefreshCw />
                <div>
                  <strong>Easy Returns</strong>
                  <span>7-day return policy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
